/**
 * `<RegionPartnerChip />` — subtle "Local partner" link inside a
 * region tile on the LocationsHub and StateHub.
 *
 * Behaviour by partner-count in the region:
 *   • 0 → renders nothing (mail-out only region).
 *   • 1 → renders "Local partner: [Business Name] →" pointing at
 *         `/pws-partner/{slug}` so users skip the RegionHub detour.
 *   • 2+ → renders "Meet local partners →" pointing at the RegionHub
 *         partner section (`#local-partners`) so we don't play
 *         favourites.
 *
 * Because it's nested inside a `<Link>` region tile, this component
 * MUST render its own `<Link>` as a sibling below the tile content
 * or use event.stopPropagation. To keep the tile a single anchor
 * (better a11y), we render this OUTSIDE the tile's `<Link>` — the
 * caller places it as a sibling within a shared wrapper.
 *
 * Data source: reuses the existing `/api/pws-partner-landing/by-
 * region/{state}/{region}` endpoint we ship for RegionHub. Results
 * are process-lifetime cached by `state.abbr|region.name` key so we
 * don't re-fetch on every re-render.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ArrowRight } from "lucide-react";
import api from "@/lib/api";

// Simple module-scoped cache keyed by `{state}|{region}`. Not
// per-request — this hub content is public and doesn't change
// per user session.
const _cache = new Map();

async function fetchRegionPartners(stateAbbr, regionName) {
  const key = `${stateAbbr}|${regionName}`;
  if (_cache.has(key)) return _cache.get(key);
  try {
    // Query-param form — region names can contain "/" (e.g. "Tweed /
    // Byron Border Coast") which breaks the path-segment route.
    const { data } = await api.get("/pws-partner-landing/by-region", {
      params: { state: stateAbbr, region: regionName },
    });
    const rows = data?.rows || [];
    _cache.set(key, rows);
    return rows;
  } catch {
    return [];
  }
}

export default function RegionPartnerChip({ state, region, regionHubHref }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchRegionPartners(state?.abbr || "", region?.name || "");
      if (!cancelled) setRows(r);
    })();
    return () => { cancelled = true; };
  }, [state, region]);

  // Loading — render nothing (avoid layout jitter). The tile still
  // navigates to the RegionHub, so no CTA is lost.
  if (rows === null || rows.length === 0) return null;

  // Multi-partner case → link back to the RegionHub partner section.
  if (rows.length > 1) {
    return (
      <Link
        to={`${regionHubHref}#local-partners`}
        data-testid={`region-partner-chip-multi-${region.slug}`}
        className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 text-cyan-800 px-3 py-1 text-[12px] font-medium hover:bg-cyan-100 transition"
      >
        <Users className="w-3.5 h-3.5" />
        Meet {rows.length} local partners
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    );
  }

  // Single partner case → direct link to the partner's storefront.
  const p = rows[0];
  return (
    <Link
      to={`/pws-partner/${p.slug}`}
      data-testid={`region-partner-chip-${region.slug}`}
      className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 text-cyan-800 px-3 py-1 text-[12px] font-medium hover:bg-cyan-100 transition max-w-full"
      title={p.business_name}
    >
      <Users className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">Local partner: {p.business_name}</span>
      <ArrowRight className="w-3.5 h-3.5 shrink-0" />
    </Link>
  );
}
